import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ExpenseResponse {
  record_id: string; expense_id: string; brain_id: string;
  service_name: string; category: string;
  amount_usd_cents: number; amount_ils_agorot: number; incurred_at: string;
}
interface ListExpensesResponse { expenses: ExpenseResponse[]; total: number; }

// ── Constants ─────────────────────────────────────────────────────────────────

export const EXPENSE_CATEGORIES = ['LLM', 'infrastructure', 'api_cost', 'other'];

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const CATEGORY_COLORS: Record<string, string> = {
  LLM: '#7dd3fc',
  infrastructure: '#a78bfa',
  api_cost: '#6ee7b7',
};

function categoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? 'var(--omega-ink-subtle)';
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceExpensesListPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('');

  const params = new URLSearchParams();
  if (dateFrom) params.set('from', dateFrom);
  if (dateTo) params.set('to', dateTo);
  if (category) params.set('category', category);
  const qs = params.toString();

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'expenses', 'list', dateFrom, dateTo, category],
    queryFn: () => apiFetch<ListExpensesResponse>(`/finance/expenses${qs ? '?' + qs : ''}`),
    refetchInterval: 30_000,
  });

  const expenses = data?.expenses ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Expenses', to: '/omega/finance/expenses' },
        { label: 'List' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Expense list
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {expenses.length}{expenses.length !== data.total ? `/${data.total}` : ''}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          style={inputStyle}
          title="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          style={inputStyle}
          title="To date"
        />
        <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
          <option value="">All categories</option>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(dateFrom || dateTo || category) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); setCategory(''); }}
            style={{ ...inputStyle, cursor: 'pointer', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Column headers */}
      {!isPending && !isError && expenses.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 100px 80px 110px 110px',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Date', 'Service', 'Category', 'Brain', 'USD', 'ILS'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {!isPending && !isError && expenses.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No expenses to display.
        </div>
      )}

      {expenses.map(e => (
        <Link
          key={e.expense_id}
          to={`/omega/finance/expenses/${e.expense_id}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr 100px 80px 110px 110px',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={ev => { (ev.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={ev => { (ev.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {fmtDate(e.incurred_at)}
          </span>
          <span style={{ fontSize: '9.5pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {e.service_name}
          </span>
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: `1px solid ${categoryColor(e.category)}`,
            color: categoryColor(e.category),
            letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
            justifySelf: 'start', whiteSpace: 'nowrap',
          }}>
            {e.category}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {e.brain_id}
          </span>
          <span style={{ fontSize: '9pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)' }}>
            {fmtUsd(e.amount_usd_cents)}
          </span>
          <span style={{ fontSize: '9pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)' }}>
            {fmtIls(e.amount_ils_agorot)}
          </span>
        </Link>
      ))}
    </div>
  );
}
